import { FocusEvent, KeyboardEvent, MouseEvent, useEffect, useId, useRef, useState } from 'react';

import { Chevron, ItemLink, List, Panel, Trigger, Wrapper } from './styles';
import { NavDropdownItem, NavDropdownProps } from './types';

// Forgives a pointer that briefly overshoots the panel edge on its way to an item.
const HOVER_CLOSE_DELAY_MS = 150;

const NavDropdown = <T extends NavDropdownItem>({
	label,
	items,
	textColor,
	onItemClick,
}: NavDropdownProps<T>) => {
	const [isOpen, setIsOpen] = useState(false);
	const wrapperRef = useRef<HTMLDivElement>(null);
	const triggerRef = useRef<HTMLButtonElement>(null);
	const closeTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);
	// How the panel opened decides how it closes. A keyboard-opened panel ignores
	// mouse-leave, which would hide the items out from under focus. A mouse click
	// lands right after the hover that opened the panel, and must not toggle it shut.
	const openedWithRef = useRef<'hover' | 'pointer' | 'keyboard'>(undefined);
	const panelId = useId();

	const cancelScheduledClose = () => clearTimeout(closeTimeoutRef.current);

	useEffect(() => cancelScheduledClose, []);

	useEffect(() => {
		if (!isOpen) {
			return;
		}
		const onDocumentPointerDown = (event: PointerEvent) => {
			if (!wrapperRef.current?.contains(event.target as Node)) {
				setIsOpen(false);
			}
		};
		// Hovering opens the panel without moving focus into it, so the wrapper's keydown
		// handler never sees Escape. Dismissing from here leaves focus where the user had it.
		const onDocumentKeyDown = (event: globalThis.KeyboardEvent) => {
			if (event.key === 'Escape') {
				setIsOpen(false);
			}
		};
		document.addEventListener('pointerdown', onDocumentPointerDown);
		document.addEventListener('keydown', onDocumentKeyDown);
		return () => {
			document.removeEventListener('pointerdown', onDocumentPointerDown);
			document.removeEventListener('keydown', onDocumentKeyDown);
		};
	}, [isOpen]);

	const onMouseEnter = () => {
		cancelScheduledClose();
		if (!isOpen) {
			openedWithRef.current = 'hover';
			setIsOpen(true);
		}
	};

	const onMouseLeave = () => {
		if (openedWithRef.current === 'keyboard') {
			return;
		}
		closeTimeoutRef.current = setTimeout(() => {
			// A hover- or click-opened panel can still be tabbed into, so focus is checked
			// when the delay ends rather than when it starts. The trigger is excluded: a
			// click focuses it, and it stays visible when the panel closes.
			const focusedElement = document.activeElement;
			if (focusedElement !== triggerRef.current && wrapperRef.current?.contains(focusedElement)) {
				return;
			}
			setIsOpen(false);
		}, HOVER_CLOSE_DELAY_MS);
	};

	const onTriggerClick = (event: MouseEvent) => {
		cancelScheduledClose();
		// detail is the click count; keyboard and screen-reader activation report 0.
		const isPointerClick = event.detail > 0;
		if (isOpen && isPointerClick && openedWithRef.current === 'hover') {
			openedWithRef.current = 'pointer';
			return;
		}
		if (!isOpen) {
			openedWithRef.current = isPointerClick ? 'pointer' : 'keyboard';
		}
		setIsOpen(!isOpen);
	};

	const onKeyDown = (event: KeyboardEvent) => {
		if (event.key === 'Escape' && isOpen) {
			setIsOpen(false);
			triggerRef.current?.focus();
		}
	};

	// Closes the panel when focus moves to an element outside the dropdown, e.g. tabbing
	// past the last item. relatedTarget is the element receiving focus. When it's null,
	// focus went to the page itself, which Safari does when you press on a link; closing
	// then would hide the panel before the link's click registers. Clicks outside the
	// dropdown are handled by the pointerdown listener instead.
	const onBlur = (event: FocusEvent) => {
		if (event.relatedTarget && !wrapperRef.current?.contains(event.relatedTarget)) {
			setIsOpen(false);
		}
	};

	const onLinkClick = (event: MouseEvent, item: T) => {
		event.preventDefault();
		setIsOpen(false);
		onItemClick(item);
	};

	return (
		<Wrapper
			ref={wrapperRef}
			onMouseEnter={onMouseEnter}
			onMouseLeave={onMouseLeave}
			onKeyDown={onKeyDown}
			onBlur={onBlur}
		>
			<Trigger
				ref={triggerRef}
				type="button"
				// Safari's default Tab order skips buttons and links without an explicit tabIndex (here and on the items).
				tabIndex={0}
				aria-expanded={isOpen}
				aria-controls={panelId}
				onClick={onTriggerClick}
				$textColor={textColor}
			>
				{label}
				<Chevron aria-hidden />
			</Trigger>
			<Panel id={panelId} hidden={!isOpen}>
				<List>
					{items.map(item => (
						<li key={item.label}>
							<ItemLink href={item.href} tabIndex={0} onClick={event => onLinkClick(event, item)}>
								{item.label}
							</ItemLink>
						</li>
					))}
				</List>
			</Panel>
		</Wrapper>
	);
};

export default NavDropdown;
